import { useLayoutEffect, useRef, type CSSProperties } from "react";
import type { ClientPageKey } from "../app/product-router.js";
import "../styles/approved-scene.css";

const scenes = {
  settings: {
    image: "/img/backgrounds/clubhouse-sunset.webp",
    anchor: ".fr-more-subpage-content",
  },
  clubhouse: {
    image: "/img/backgrounds/clubhouse-sunset.webp",
    anchor: ".fr-home-circle, .fr-home-invitation, .fr-home-loading",
  },
  rivalry: {
    image: "/img/backgrounds/rivalry.webp",
    anchor: ".r3-content, .rv-hub-body",
  },
  fgl: { image: "/img/backgrounds/season.webp", anchor: ".fr-season-content" },
  feed: {
    image: "/img/backgrounds/clubhouse-sunset.webp",
    anchor: ".feed-list",
  },
};

/** A3 fixed art, with the clear-to-frosted edge following the content. */
export function ApprovedScene({
  page,
}: {
  page: ClientPageKey;
}): React.JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null);
  const scene = page in scenes ? scenes[page as keyof typeof scenes] : null;
  useLayoutEffect(() => {
    const surface = ref.current;
    const scroller =
      surface?.parentElement?.querySelector<HTMLElement>(".fr-main");
    if (!surface || !scroller || !scene) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const target = scroller.querySelector<HTMLElement>(scene.anchor);
      const rect = surface.getBoundingClientRect();
      const boundary = target
        ? target.getBoundingClientRect().top - rect.top
        : 330 - scroller.scrollTop;
      surface.style.setProperty("--fr-scene-boundary", `${boundary}px`);
      surface.style.setProperty("--fr-scene-width", `${rect.width}px`);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const resize = new ResizeObserver(schedule);
    resize.observe(scroller);
    const mutation = new MutationObserver(schedule);
    mutation.observe(scroller, { childList: true, subtree: true });
    scroller.addEventListener("scroll", schedule, { passive: true });
    update();
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      mutation.disconnect();
      scroller.removeEventListener("scroll", schedule);
    };
  }, [scene]);
  if (!scene) return null;
  return (
    <div
      ref={ref}
      className={`fr-approved-scene fr-approved-scene--${page}`}
      aria-hidden="true"
      style={{ "--fr-scene-image": `url(${scene.image})` } as CSSProperties}
    >
      <div className="fr-approved-scene-frost" />
      <div className="fr-approved-scene-clear" />
    </div>
  );
}
